import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { submitReview, fetchReviews } from '../../store/slices/reviewsSlice';
import { FaStar, FaUser } from 'react-icons/fa';
import { toast } from 'react-toastify';

const ProductReviews = ({ productId }) => {
  const dispatch = useDispatch();
  const { items: reviews, summary, pagination, loading, submitting, error } = useSelector((state) => state.reviews);
  const [showReviewForm, setShowReviewForm] = useState(false);
  
  // Fetch reviews when component mounts or productId changes
  useEffect(() => {
    if (productId) {
      dispatch(fetchReviews({ productId }));
    }
  }, [productId, dispatch]);
  const [formData, setFormData] = useState({
    userName: '',
    userEmail: '',
    rating: 5,
    comment: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleRatingClick = (rating) => {
    setFormData({ ...formData, rating });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.userName || !formData.userEmail || !formData.comment) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!productId) {
      toast.error('Product ID is missing');
      return;
    }

    try {
      console.log('Submitting review with data:', {
        productId,
        userName: formData.userName,
        userEmail: formData.userEmail,
        rating: parseInt(formData.rating),
        hasComment: !!formData.comment
      });

      const result = await dispatch(submitReview({
        productId,
        userName: formData.userName,
        userEmail: formData.userEmail,
        rating: parseInt(formData.rating),
        comment: formData.comment
      })).unwrap();
      
      console.log('Review submission successful:', result);
      
      toast.success(result.message || 'Review submitted successfully! It will be published after admin approval.');
      setFormData({
        userName: '',
        userEmail: '',
        rating: 5,
        comment: ''
      });
      setShowReviewForm(false);
      
      // Refetch reviews to update the list (though it won't show until approved)
      setTimeout(() => {
        dispatch(fetchReviews({ productId }));
      }, 500);
    } catch (error) {
      console.error('Review submission error:', error);
      const errorMessage = typeof error === 'string' ? error : (error?.message || 'Failed to submit review');
      toast.error(errorMessage);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const renderStars = (rating, interactive = false, onClick = null) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type={interactive ? 'button' : 'div'}
            onClick={interactive && onClick ? () => onClick(star) : undefined}
            className={interactive ? 'cursor-pointer' : 'cursor-default'}
          >
            <FaStar
              className={`text-lg ${
                star <= rating
                  ? 'text-yellow-400 fill-current'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="mt-12 border-t pt-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-heading text-3xl font-bold text-black mb-2">Customer Reviews</h2>
          {summary.totalReviews > 0 && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-3xl font-bold text-black">{summary.averageRating}</span>
                <div className="flex flex-col">
                  {renderStars(Math.round(summary.averageRating))}
                  <span className="text-sm text-gray-600">{summary.totalReviews} reviews</span>
                </div>
              </div>
            </div>
          )}
        </div>
        <button
          onClick={() => setShowReviewForm(!showReviewForm)}
          className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary-dark transition font-semibold"
        >
          Write a Review
        </button>
      </div>

      {/* Review Form */}
      {showReviewForm && (
        <div className="bg-gray-50 rounded-lg p-6 mb-8">
          <h3 className="font-heading text-xl font-bold text-black mb-4">Write Your Review</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 mb-2 font-semibold">Your Name *</label>
                <input
                  type="text"
                  name="userName"
                  value={formData.userName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter your name"
                />
              </div>
              <div>
                <label className="block text-gray-700 mb-2 font-semibold">Your Email *</label>
                <input
                  type="email"
                  name="userEmail"
                  value={formData.userEmail}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <label className="block text-gray-700 mb-2 font-semibold">Rating *</label>
              <div className="flex items-center gap-2">
                {renderStars(formData.rating, true, handleRatingClick)}
                <span className="text-gray-600 ml-2">{formData.rating} out of 5</span>
              </div>
            </div>

            <div>
              <label className="block text-gray-700 mb-2 font-semibold">Your Review *</label>
              <textarea
                name="comment"
                value={formData.comment}
                onChange={handleInputChange}
                required
                rows="5"
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Share your experience with this product..."
                maxLength={1000}
              />
              <p className="text-sm text-gray-500 mt-1">{formData.comment.length}/1000 characters</p>
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                disabled={submitting}
                className="bg-primary text-white px-8 py-3 rounded-lg hover:bg-primary-dark transition font-semibold disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Submit Review'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowReviewForm(false);
                  setFormData({
                    userName: '',
                    userEmail: '',
                    rating: 5,
                    comment: ''
                  });
                }}
                className="bg-gray-200 text-gray-700 px-8 py-3 rounded-lg hover:bg-gray-300 transition font-semibold"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Rating Distribution */}
      {summary.totalReviews > 0 && (
        <div className="bg-gray-50 rounded-lg p-6 mb-8">
          <h3 className="font-semibold text-lg mb-4 text-black">Rating Breakdown</h3>
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((rating) => {
              const count = summary.ratingDistribution[rating] || 0;
              const percentage = summary.totalReviews > 0 ? (count / summary.totalReviews) * 100 : 0;
              return (
                <div key={rating} className="flex items-center gap-3">
                  <span className="text-sm font-semibold w-8">{rating}</span>
                  <FaStar className="text-yellow-400" />
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-yellow-400 h-2 rounded-full"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-600 w-12 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-600">Error loading reviews: {error}</p>
        </div>
      )}

      {/* Reviews List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg p-6 border animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/4 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-1/3 mb-4" />
              <div className="h-20 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      ) : reviews.length > 0 ? (
        <div className="space-y-6">
          {reviews.map((review) => (
            <div key={review._id} className="bg-white rounded-lg p-6 border border-gray-200">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white">
                    <FaUser />
                  </div>
                  <div>
                    <h4 className="font-semibold text-black">{review.userName}</h4>
                    <p className="text-sm text-gray-500">{formatDate(review.createdAt)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {renderStars(review.rating)}
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">{review.comment}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600 text-lg mb-2">No reviews yet</p>
          <p className="text-gray-500">Be the first to review this product!</p>
        </div>
      )}
    </div>
  );
};

export default ProductReviews;

